import { PartialNullable } from "../../Extensions";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { Button } from "./Button";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { NAMESPACE } from "./_Namespace";

import "./Card.scss";

export type CardPropertyValues =
    ControlPropertyValues
    & {
        buttons: Button[];
        iconClass: string;
        imageUrl: string;
        subTitle: string;
        title: string;
    };

export type CardProperties = {
    [name in keyof CardPropertyValues]: Property<CardPropertyValues[name]>
};

export interface ICard extends IControl, CardProperties
{
}

export class Card extends Control implements ICard
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Card";
    public static override className = "Card";

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _buttonContainerElementRef!: ElementRef;
    protected _contentElementRef!: ElementRef;
    protected _descriptionElementRef!: ElementRef;
    protected _extraContentElementRef!: ElementRef;
    protected _iconContainerElementRef!: ElementRef;
    protected _imageElementRef!: ElementRef;
    protected _subTitleElementRef!: ElementRef;
    protected _titleElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public buttons!: Property<Button[]>;
    public iconClass!: PropertyString;
    public imageUrl!: PropertyString;
    public subTitle!: PropertyString;
    public title!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<CardPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-card card");

        // Trigger Property Changes
        this.buttons.triggerChange();
        this.iconClass.triggerChange();
        this.imageUrl.triggerChange();
        this.subTitle.triggerChange();
        this.title.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public removeAsync()
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return Promise.reject();

        return (
            $$element.stop()
                .animate(
                    {
                        opacity: 0
                    },
                    200
                )
                .promise()
                .then(() =>
                {
                    return $$element.animate({ height: 0, marginTop: 0, marginBottom: 0 }).promise();
                })
                .then(() =>
                {
                    $$element.remove();
                })
        );
    }

    // #endregion

    // #region Protected Methods

    protected _buttonsChanged(change: PropertyChange<Button[]>)
    {
        this._buttonContainerElementRef.current.children().detach();
        this._extraContentElementRef.current.hide();

        if (!change.currentValue || !change.currentValue.length || change.currentValue.length === 0)
        {
            this._extraContentElementRef.current.hide();
            return;
        }

        change.currentValue
            .forEach(button => { button.appendTo(this._buttonContainerElementRef.current); });

        this._extraContentElementRef.current.show();
    }
    protected _iconClassChanged(change: PropertyChange<string>)
    {
        if (change.originalValue)
            this._imageElementRef.current.removeClass(change.originalValue).hide();

        if (change.currentValue)
            this._imageElementRef.current.addClass(change.currentValue).show();
    }
    protected _imageUrlChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _subTitleChanged(change: PropertyChange<string>)
    {
        this._subTitleElementRef.current.text(change.currentValue);
    }
    protected _titleChanged(change: PropertyChange<string>)
    {
        this._titleElementRef.current.text(change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: CardPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._contentElementRef = new ElementRef();
        this._iconContainerElementRef = new ElementRef();
        this._imageElementRef = new ElementRef();
        this._titleElementRef = new ElementRef();
        this._subTitleElementRef = new ElementRef();
        this._descriptionElementRef = new ElementRef();
        this._extraContentElementRef = new ElementRef();
        this._buttonContainerElementRef = new ElementRef();

        this.buttons = new Property<Button[]>(this, "System.Object, System.Private.CoreLib", propertyValues.buttons, this._buttonsChanged);
        this.iconClass = new PropertyString(this, propertyValues.iconClass, this._iconClassChanged);
        this.imageUrl = new PropertyString(this, propertyValues.imageUrl, this._imageUrlChanged);
        this.subTitle = new PropertyString(this, propertyValues.subTitle, this._subTitleChanged);
        this.title = new PropertyString(this, propertyValues.title, this._titleChanged);
    }

    protected override _createElementComplete()
    {
        super._createElementComplete();

        this._imageElementRef.current.hide();
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <span ref={this._contentElementRef} class="content">
                    <span ref={this._iconContainerElementRef}></span>
                    <i ref={this._imageElementRef} class="right floated mini ui image"><span></span></i>
                    <span ref={this._titleElementRef} class="header"></span>
                    <span ref={this._subTitleElementRef} class="meta"></span>
                    <span ref={this._descriptionElementRef} class="description"></span>
                </span>
                <span ref={this._extraContentElementRef} class="extra content">
                    <span ref={this._buttonContainerElementRef} class="ui five buttons"></span>
                </span>
            </div>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this._descriptionElementRef.current.text(change.currentValue);
    }

    // #endregion

    // #endregion
}

